import React from "react"
import styled, { css } from "styled-components"
import colors from "@constants/colors"
import breakpoints from "@constants/breakpoints"
import { h3Styles, h4Styles } from "@constants/typography"
import FlatLinkButton from "@components/buttons/FlatLinkButton"
import LinkButton from "@components/buttons/LinkButton"
import Scrollbar from "@components/Scrollbar"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"

const Container = styled.div<any>`
  display: flex;
  width: 100%;
  padding: 88px 0;
  flex-direction: column;

  @media ${breakpoints.tablet} {
    padding: calc(1 / 12 * 100vw) 0;
  }

  @media ${breakpoints.laptop} {
    padding: calc(1 / 12 * 100vw) 0;
  }

  ${({ containerStyles }) => (containerStyles ? containerStyles : "")}
`

const Header = styled.div`
  display: flex;
  padding-left: 20px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  @media ${breakpoints.tablet} {
    padding: 0 calc(1 / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    padding: 0 calc(1 / 12 * 100vw);
  }
`

const Title = styled.div`
  ${h4Styles}

  @media ${breakpoints.desktop} {
    ${h3Styles}
  }
`

export const Content = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media ${breakpoints.mobileOnly} {
    margin-bottom: -20px;
    padding: 20px 20px 40px;
  }

  @media ${breakpoints.tablet} {
    padding: 40px calc(1 / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    padding: 48px calc(1 / 12 * 100vw);
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`

const Item = styled.div`
  display: inline-flex;
  flex-direction: row;
`

export const Block = styled.div<{ useSmallCards?: boolean }>`
  ${({ useSmallCards }) => css`
    /* position: relative; */
    width: ${useSmallCards ? "max-content" : "calc(100vw - 20px - 36px)"};

    @media ${breakpoints.tablet} {
      width: ${useSmallCards ? "max-content" : "calc(4.25 / 12 * 100vw)"};
    }

    @media ${breakpoints.laptop} {
      width: ${useSmallCards ? "max-content" : "calc(3 / 12 * 100vw)"};
    }
  `}
`

export const Spacer = styled.div`
  width: 8px;
  height: 100%;
  background: transparent;

  @media ${breakpoints.laptop} {
    width: 24px;
  }
`

const FinalSpacer = styled.div`
  width: 20px;
  height: 100%;
  background: transparent;

  @media ${breakpoints.tablet} {
    width: calc(1 / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    width: calc(1 / 12 * 100vw);
  }
`

export const Button = styled(LinkButton)`
  width: 248px;
`

export const FlatButton: any = styled(FlatLinkButton)`
  width: auto;
  max-width: 50%;
`

const CustomScrollbar = styled(Scrollbar)`
  padding: 0 20px;

  @media ${breakpoints.tablet} {
    padding: 0 calc(1 / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    padding: 0 calc(1 / 12 * 100vw);
  }
`

// mobile Safari PLEASE
export const MobileSafariScrollbarHider = styled.div`
  width: 100%;
  height: 20px;
  background-color: ${colors.white};
  z-index: 10;
`

interface BlockSliderProps<T> {
  blockId?: string
  title: string
  titleShort: string
  to?: string
  toText: string
  toTextShort: string
  data: T[]
  renderBlock: (data: T) => React.ReactNode
  useBlockRefs?: boolean
  useSmallCards?: boolean
  className?: string
  containerStyles?: string
}

export const SliderContent = (props: any) => {
  return props.useBlockRefs ? (
    <Content ref={props.contentRef}>
      {props.data.map((item, index) => (
        <Item key={JSON.stringify(item)}>
          <Block
            useSmallCards={!!props.useSmallCards}
            ref={props.blockRefs[index]}
          >
            {props.renderBlock({
              ...item,
              containerRef: props.contentRef,
              blockRef: props.blockRefs[index],
            })}
          </Block>
          {index === props.data.length - 1 ? <FinalSpacer /> : <Spacer />}
        </Item>
      ))}
    </Content>
  ) : (
    <Content ref={props.contentRef}>
      {props.data.map((item, index) => (
        <Item key={JSON.stringify(item)}>
          <Block useSmallCards={!!props.useSmallCards}>
            {props.renderBlock(item)}
          </Block>
          {index === props.data.length - 1 ? <FinalSpacer /> : <Spacer />}
        </Item>
      ))}
    </Content>
  )
}

class BlockSlider<T> extends React.PureComponent<BlockSliderProps<T>> {
  contentRef = React.createRef<HTMLDivElement>()
  blockRefs = [] as any

  componentDidMount(): void {
    if (this.props.useBlockRefs) {
      this.blockRefs = this.props.data.map(() =>
        React.createRef<HTMLDivElement>()
      )
    }
  }

  render() {
    const {
      blockId,
      title,
      titleShort,
      to,
      toText,
      toTextShort,
      data,
      renderBlock,
      useBlockRefs,
      useSmallCards,
      className,
      containerStyles,
    } = this.props

    return (
      <Container
        className={className}
        containerStyles={containerStyles}
        id={blockId || undefined}
      >
        <Header>
          <Title>
            <DesktopOnly>{title}</DesktopOnly>
            <MobileAndTabletOnly>{titleShort}</MobileAndTabletOnly>
          </Title>
          {to && (
            <>
              <DesktopOnly>
                <Button to={to || ""} text={toText} />
              </DesktopOnly>
              <MobileAndTabletOnly>
                <FlatButton to={to || ""} text={toTextShort} />
              </MobileAndTabletOnly>
            </>
          )}
        </Header>
        <SliderContent
          contentRef={this.contentRef}
          blockRefs={this.blockRefs}
          data={data}
          renderBlock={renderBlock}
          useBlockRefs={useBlockRefs}
          useSmallCards={useSmallCards}
        />
        <CustomScrollbar containerRef={this.contentRef} />
        <MobileSafariScrollbarHider />
      </Container>
    )
  }
}

export default BlockSlider
